import SenderSelect, { useMe } from '../components/SenderSelect';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, Plus, Send, Clock, Trash2, Users, Save, FlaskConical, Ban, RefreshCw, Eye, Paperclip, MailSearch, Copy, ChevronDown, Globe, Sparkles, Loader2, Pause, Play,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from '../components/ui/command';
import { Checkbox } from '../components/ui/checkbox';
import { X, ListPlus } from 'lucide-react';
import EmailBuilder from '../components/EmailBuilder';
import WebinarAutomations from '../components/WebinarAutomations';
import { blocksToHtml } from '../lib/emailBlocks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = `${API}/admin/bulk-email`;
const ENRICH = `${API}/admin/dormant-enrich`;

const AUDIENCE_TYPES = [
  { type: 'all_suppliers', label: 'All suppliers with a login' },
  { type: 'confirmed_suppliers', label: 'Confirmed (paying) suppliers' },
  { type: 'suppliers_by_tier', label: 'Suppliers by tier' },
  { type: 'tech_suppliers', label: 'All tech suppliers (directory)' },
  { type: 'care_providers', label: 'All care providers (directory)' },
  { type: 'webinar', label: 'Webinar registrants' },
  { type: 'everyone', label: 'Everyone (suppliers + lists + leads)' },
  { type: 'leads', label: 'Leads & waitlist' },
  { type: 'paste', label: 'Pasted list of addresses' },
  { type: 'segment', label: 'Saved segment' },
];

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  scheduled: 'bg-amber-100 text-amber-800',
  sending: 'bg-blue-100 text-blue-800',
  sent: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
};

const emptyDraft = (me = null) => ({
  sender_member_id: me,
  name: 'Untitled campaign', subject: '', html_body: '', design_json: [], attachments: [],
  from_name: 'Care Tech Compare', from_email: '', reply_to: '',
  audience: { type: 'paste', emails: '', lists: [], segment_id: '', webinar_slug: '', tier: '' },
});

export default function BulkEmail() {
  const [campaigns, setCampaigns] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sel, setSel] = useState(null);          // currently edited campaign (with id once saved)
  const [preview, setPreview] = useState(null);   // {count, sample}
  const [listName, setListName] = useState(null);
  const [savingList, setSavingList] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [composeMode, setComposeMode] = useState('design'); // 'design' | 'html'
  const [tab, setTab] = useState('campaigns'); // 'campaigns' | 'automations'
  const [stats, setStats] = useState(null);
  const [recips, setRecips] = useState(null);
  const [recipFilter, setRecipFilter] = useState('opened');
  const [recipSearch, setRecipSearch] = useState('');
  const [recipPage, setRecipPage] = useState(0);
  const [recipLoading, setRecipLoading] = useState(false);
  const [showRecips, setShowRecips] = useState(false);
  const RECIP_PAGE = 25;
  const [segOptions, setSegOptions] = useState({ webinars: [], tiers: [] });
  const [lists, setLists] = useState([]);
  const [listMenuOpen, setListMenuOpen] = useState(false);
  const me = useMe();
  const [enrich, setEnrich] = useState(null);
  const [care, setCare] = useState(null);
  const [domains, setDomains] = useState(null);  const [bestTimes, setBestTimes] = useState(null);
  const [abandoned, setAbandoned] = useState(0);

  const recoverAbandoned = async () => {
    if (!window.confirm(`Send recovery emails to ${abandoned} supplier(s) who started a claim but didn't finish?`)) return;
    try {
      const { data } = await axios.post(`${BASE}/abandoned-claims/nudge`);
      toast.success(`Recovery emails sent: ${data.sent} of ${data.candidates}`);
      const ac = await axios.get(`${BASE}/abandoned-claims`); setAbandoned(ac.data?.count || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send recovery emails'); }
  };

  const loadAll = useCallback(async () => {
    try {
      const [c, a, s, t, o, bt] = await Promise.all([
        axios.get(`${BASE}/campaigns`),
        axios.get(`${BASE}/audiences`),
        axios.get(`${BASE}/segments`),
        axios.get(`${BASE}/templates`),
        axios.get(`${BASE}/segment-options`),
        axios.get(`${BASE}/best-send-times`),
      ]);
      setCampaigns(c.data.campaigns || []);
      setAudiences(a.data.audiences || []);
      setSegments(s.data.segments || []);
      setTemplates(t.data.templates || []);
      setSegOptions(o.data || { webinars: [], tiers: [] });
      setBestTimes(bt.data || null);
      try { const ll = await axios.get(`${API}/lists?object_type=contact`); setLists(ll.data?.lists || []); } catch (e) { /* ignore */ }
      try { const ac = await axios.get(`${BASE}/abandoned-claims`); setAbandoned(ac.data?.count || 0); } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error('Failed to load bulk email data');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  // light polling so "sending" progress + scheduled fires reflect live
  useEffect(() => {
    const id = setInterval(loadAll, 15000);
    return () => clearInterval(id);
  }, [loadAll]);

  const openNew = () => { setSel(emptyDraft(me)); setPreview(null); setScheduleAt(''); setComposeMode('design'); };
  const openCampaign = (c) => {
    setSel({ design_json: [], ...c, audience: { type: 'confirmed_suppliers', emails: '', segment_id: '', webinar_slug: '', tier: '', ...(c.audience || {}) } });
    setPreview(null); setScheduleAt('');
    setComposeMode((c.design_json && c.design_json.length) ? 'design' : (c.html_body ? 'html' : 'design'));
  };

  const setBlocks = (blocks) => setSel((p) => ({ ...p, design_json: blocks, html_body: blocksToHtml(blocks) }));

  const uploadAttachment = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${BASE}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSel((p) => ({ ...p, attachments: [...(p.attachments || []), data] }));
      toast.success(`Attached ${data.filename}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Attachment upload failed'); }
  };
  const removeAttachment = (id) => setSel((p) => ({ ...p, attachments: (p.attachments || []).filter((a) => a.id !== id) }));
  const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n || 0} B`);

  // Load open/click stats when viewing a sent/sending campaign (auto-refreshes with the poll).
  useEffect(() => {
    if (!sel?.id || !(sel.status === 'sent' || sel.status === 'sending')) { setStats(null); return; }
    let live = true;
    const fetchStats = () => axios.get(`${BASE}/campaigns/${sel.id}/stats`).then((r) => { if (live) setStats(r.data); }).catch(() => {});
    fetchStats();
    const id = setInterval(fetchStats, 15000);
    return () => { live = false; clearInterval(id); };
  }, [sel?.id, sel?.status]);

  // Reset recipient panel when switching campaigns.
  useEffect(() => { setShowRecips(false); setRecips(null); setRecipPage(0); setRecipSearch(''); setRecipFilter('opened'); }, [sel?.id]);

  const fetchRecips = useCallback(async (page = 0) => {
    if (!sel?.id) return;
    setRecipLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/campaigns/${sel.id}/recipients`, {
        params: { filter: recipFilter, search: recipSearch, skip: page * RECIP_PAGE, limit: RECIP_PAGE },
      });
      setRecips(data);
      setRecipPage(page);
    } catch { toast.error('Could not load recipient activity'); }
    finally { setRecipLoading(false); }
  }, [sel?.id, recipFilter, recipSearch]);

  useEffect(() => { if (showRecips) fetchRecips(0); /* eslint-disable-next-line */ }, [showRecips, recipFilter]);

  const downloadRecipCsv = async () => {
    try {
      const { data } = await axios.get(`${BASE}/campaigns/${sel.id}/recipients.csv`, {
        params: { filter: recipFilter, search: recipSearch }, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `campaign-${sel.id}-${recipFilter}-recipients.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not export CSV'); }
  };

  const setField = (k, v) => setSel((p) => ({ ...p, [k]: v }));
  const setAud = (k, v) => setSel((p) => ({ ...p, audience: { ...p.audience, [k]: v } }));

  // Selected recipient lists (stackable). Derives from whatever the campaign audience is.
  const selectedListIds = (() => {
    const a = sel?.audience || {};
    if (a.lists) return a.lists;
    if (a.type === 'crm_list' && a.list_id) return [a.list_id];
    if (a.type === 'combo') return (a.include || []).filter((i) => i.type === 'crm_list').map((i) => i.list_id);
    return [];
  })();

  const toggleList = (id) => {
    const cur = new Set(selectedListIds);
    cur.has(id) ? cur.delete(id) : cur.add(id);
    const ids = [...cur];
    setSel((p) => {
      let audience;
      if (ids.length === 0) audience = { type: 'paste', emails: p.audience?.emails || '', lists: [] };
      else if (ids.length === 1) audience = { type: 'crm_list', list_id: ids[0], lists: ids };
      else audience = { type: 'combo', include: ids.map((x) => ({ type: 'crm_list', list_id: x })), lists: ids };
      return { ...p, audience };
    });
    setPreview(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: sel.name, subject: sel.subject, html_body: sel.html_body, design_json: sel.design_json || [], base_url: process.env.REACT_APP_BACKEND_URL, attachments: sel.attachments || [], audience: sel.audience, from_name: sel.from_name || '', from_email: sel.from_email || '', reply_to: sel.reply_to || '', sender_member_id: sel.sender_member_id || '' };
      let data;
      if (sel.id) ({ data } = await axios.put(`${BASE}/campaigns/${sel.id}`, body));
      else ({ data } = await axios.post(`${BASE}/campaigns`, body));
      setSel({ design_json: [], ...data, audience: { type: 'confirmed_suppliers', emails: '', segment_id: '', webinar_slug: '', tier: '', ...(data.audience || {}) } });
      await loadAll();
      toast.success('Saved');
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const doPreview = async () => {
    try {
      const { data } = await axios.post(`${BASE}/preview`, sel.audience);
      setPreview(data);
    } catch (e) { toast.error('Could not preview recipients'); }
  };

  const saveAsList = async () => {
    if (!listName?.trim()) return toast.error('Give the list a name');
    setSavingList(true);
    try {
      const { data } = await axios.post(`${BASE}/save-as-list`, { name: listName.trim(), audience: sel.audience });
      toast.success(`Saved "${data.name}" with ${data.added} contacts${data.not_in_crm ? ` (${data.not_in_crm} not in the CRM yet, so left out)` : ''}`);
      setListName(null);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the list'); }
    finally { setSavingList(false); }
  };

  const ensureSaved = async () => (sel.id ? sel : await save());

  const sendNow = async () => {
    const c = await ensureSaved();
    if (!c?.id) return;
    if (!window.confirm('Send this campaign now to everyone in the audience?')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/campaigns/${c.id}/send-now`);
      toast.success(`Sending to ${data.total_recipients} recipients`);
      await loadAll();
      setSel((p) => ({ ...p, status: 'sending', total_recipients: data.total_recipients, sent_count: 0, failed_count: 0 }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  const suggestSlot = () => {
    if (!bestTimes?.best_weekday) return;
    const map = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
    const targetDow = map[bestTimes.best_weekday];
    const hour = bestTimes.best_hour ?? 9;
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, 0, 0);
    let add = (targetDow - d.getDay() + 7) % 7;
    if (add === 0 && d <= now) add = 7;
    d.setDate(d.getDate() + add);
    const pad = (n) => String(n).padStart(2, '0');
    setScheduleAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:00`);
    toast.success(`Set to next ${bestTimes.best_weekday} at ${pad(hour)}:00 UK - your best-performing slot`);
  };

  const doSchedule = async () => {
    if (!scheduleAt) return toast.error('Pick a date and time first');
    const c = await ensureSaved();
    if (!c?.id) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/campaigns/${c.id}/schedule`,
        { scheduled_at: scheduleAt, timezone: 'Europe/London' });
      toast.success(`Scheduled for ${new Date(data.scheduled_at).toLocaleString('en-GB')}`);
      await loadAll();
      setSel((p) => ({ ...p, status: 'scheduled', scheduled_at: data.scheduled_at }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Schedule failed'); }
    finally { setBusy(false); }
  };

  const cancelSchedule = async () => {
    setBusy(true);
    try {
      await axios.post(`${BASE}/campaigns/${sel.id}/cancel`);
      toast.success('Schedule cancelled');
      await loadAll();
      setSel((p) => ({ ...p, status: 'draft', scheduled_at: null }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
    finally { setBusy(false); }
  };

  const togglePause = async (c) => {
    const action = c.paused ? 'resume' : 'pause';
    try {
      await axios.post(`${BASE}/campaigns/${c.id}/${action}`);
      toast.success(c.paused ? 'Campaign resumed' : 'Campaign paused - nothing more will send until you resume it');
      if (sel?.id === c.id) setSel((p) => ({ ...p, paused: !c.paused }));
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || `Could not ${action} campaign`); }
  };
  const canPause = (c) => ['scheduled', 'sending', 'queued'].includes(c?.status);

  const testSend = async () => {
    const c = await ensureSaved();
    if (!c?.id) return;
    if (!testEmail) return toast.error('Enter an email to test');
    setBusy(true);
    try {
      await axios.post(`${BASE}/campaigns/${c.id}/test-send`, { email: testEmail });
      toast.success(`Test sent to ${testEmail}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test send failed'); }
    finally { setBusy(false); }
  };

  const runClaimNudge = async () => {
    if (!window.confirm('Email unclaimed suppliers (that have a contact email) inviting them to claim their listing?')) return;
    try {
      const { data } = await axios.post(`${BASE}/claim-nudge?limit=200`);
      toast.success(`Claim nudge: sent ${data.sent}, skipped ${data.skipped_no_email} with no email (of ${data.total_unclaimed} unclaimed)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Claim nudge failed'); }
  };

  // Dormant email enrichment - crawl supplier websites for public contact emails.
  const fetchEnrich = useCallback(async () => {
    try { const { data } = await axios.get(`${ENRICH}/status`); setEnrich(data); return data; }
    catch (e) { return null; }
  }, []);
  useEffect(() => { fetchEnrich(); }, [fetchEnrich]);
  useEffect(() => {
    if (enrich?.status !== 'running') return;
    const id = setInterval(fetchEnrich, 4000);
    return () => clearInterval(id);
  }, [enrich?.status, fetchEnrich]);

  const fetchCare = useCallback(async () => {
    try { const { data } = await axios.get(`${ENRICH}/care-provider/status`); setCare(data); return data; }
    catch (e) { return null; }
  }, []);
  useEffect(() => { fetchCare(); }, [fetchCare]);
  useEffect(() => {
    if (care?.status !== 'running') return;
    const id = setInterval(fetchCare, 4000);
    return () => clearInterval(id);
  }, [care?.status, fetchCare]);

  const fetchDomains = useCallback(async () => {
    try { const { data } = await axios.get(`${ENRICH}/care-provider/find-domains/status`); setDomains(data); return data; }
    catch (e) { return null; }
  }, []);
  useEffect(() => { fetchDomains(); }, [fetchDomains]);
  useEffect(() => {
    if (domains?.status !== 'running') return;
    const id = setInterval(fetchDomains, 4000);
    return () => clearInterval(id);
  }, [domains?.status, fetchDomains]);

  const runHunter = async () => {
    if (!window.confirm('Enrich tech-supplier listings via Hunter.io (uses your Hunter credits)? Fill-only.')) return;
    try {
      await axios.post(`${ENRICH}/hunter/start`);
      toast.success('Hunter.io enrichment started in the background');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start Hunter enrichment'); }
  };

  const runCareProviders = async () => {
    try {
      const s = await fetchCare();
      const n = s?.with_website ?? '';
      if (!window.confirm(`Enrich care-provider contact emails? For ${n} providers with a website, we crawl their site first, then fall back to Hunter.io (uses credits) only if nothing is found. Fill-only, runs in the background.`)) return;
      await axios.post(`${ENRICH}/care-provider/start`);
      toast.success('Care-provider enrichment started - website crawl first, Hunter fallback');
      setTimeout(fetchCare, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start care-provider enrichment'); }
  };

  const runFindDomains = async () => {
    try {
      const s = await fetchDomains();
      const n = s?.not_yet_tried ?? s?.without_website ?? '';
      if (!window.confirm(`Find websites for care providers that don't have one yet? We search the web by name + location (free) for up to ${n} providers, then save any confident match so you can enrich their emails next. Runs in the background and can take a while.`)) return;
      await axios.post(`${ENRICH}/care-provider/find-domains/start`);
      toast.success('Website discovery started - searching for provider websites in the background');
      setTimeout(fetchDomains, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start website discovery'); }
  };

  const linkLists = async () => {
    if (!window.confirm('Attach the emails in your saved lists (existing/lapsed/prospects) to matching supplier listings by domain and name?')) return;
    try {
      const { data } = await axios.post(`${BASE}/link-list-emails`);
      const tot = (data.lists || []).reduce((s, l) => s + l.attached, 0);
      toast.success(`Linked ${tot} emails to listings - ${(data.lists || []).map((l) => `${l.list}: ${l.attached}/${l.emails}`).join(' · ')}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Linking failed'); }
  };

  const importSupplierContacts = async () => {
    if (!window.confirm('Import all Care Tech Suppliers that have an email as contacts, and build a fresh "Care Tech Suppliers" list? Safe to re-run (de-dupes by email).')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/import-supplier-contacts`);
      toast.success(`Imported ${data.imported} suppliers into "${data.list_name}" (${data.created} new, ${data.updated} updated). Select it under Recipient lists.`);
      try { const ll = await axios.get(`${API}/lists?object_type=contact`); setLists(ll.data?.lists || []); } catch (e) { /* ignore */ }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  const runEnrich = async () => {
    const s = await fetchEnrich();
    const n = s?.with_website ?? '';
    if (!window.confirm(`Crawl ${n} unclaimed supplier websites for public contact emails? This runs in the background and only fills missing emails.`)) return;
    try {
      await axios.post(`${ENRICH}/start`);
      toast.success('Enrichment started - crawling supplier websites in the background');
      setTimeout(fetchEnrich, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start enrichment'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    await axios.delete(`${BASE}/campaigns/${id}`);
    if (sel?.id === id) setSel(null);
    await loadAll();
    toast.success('Deleted');
  };

  // Clone a campaign (subject, body, design, attachments, audience) into a new editable DRAFT.
  const duplicate = (c) => {
    setSel({
      name: `Copy of ${c.name || 'Untitled campaign'}`,
      subject: c.subject || '',
      html_body: c.html_body || '',
      design_json: c.design_json || [],
      attachments: c.attachments || [],
      audience: { type: 'confirmed_suppliers', emails: '', segment_id: '', webinar_slug: '', tier: '', ...(c.audience || {}) },
    });
    setPreview(null); setScheduleAt('');
    setComposeMode((c.design_json && c.design_json.length) ? 'design' : (c.html_body ? 'html' : 'design'));
    toast.success('Duplicated to a new draft - edit and save when ready');
  };

  const saveTemplate = async () => {
    if (!sel?.html_body) return toast.error('Add some content first');
    const name = window.prompt('Template name?', sel.name || 'Template');
    if (!name) return;
    await axios.post(`${BASE}/templates`, { name, subject: sel.subject, html_body: sel.html_body });
    await loadAll();
    toast.success('Template saved');
  };

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (t) { setSel((p) => ({ ...p, subject: p.subject || t.subject, html_body: t.html_body })); setComposeMode('html'); }
  };

  const saveSegment = async () => {
    const name = window.prompt('Segment name?');
    if (!name) return;
    await axios.post(`${BASE}/segments`, { name, audience: sel.audience });
    await loadAll();
    toast.success('Segment saved');
  };

  const isLocked = sel && (sel.status === 'sending' || sel.status === 'sent');

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="bulk-email-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-[#00A79D]/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-[#00A79D]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0B2942]">Bulk Email</h1>
            <p className="text-sm text-slate-500">Compose, schedule and send to your directory audiences.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={loadAll} data-testid="bulk-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="bulk-enrichment-menu">
                {(enrich?.status === 'running' || care?.status === 'running' || domains?.status === 'running')
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin text-[#00A79D]" />
                  : <Sparkles className="w-4 h-4 mr-1.5" />}
                Enrichment
                <ChevronDown className="w-4 h-4 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs text-slate-400 uppercase tracking-wider">Suppliers</DropdownMenuLabel>
              <DropdownMenuItem onSelect={runEnrich} disabled={enrich?.status === 'running'} data-testid="bulk-enrich-dormant" className="cursor-pointer">
                <MailSearch className="w-4 h-4 mr-2 text-slate-500" />
                <div className="flex-1">
                  <div>Enrich supplier emails</div>
                  <div className="text-[11px] text-slate-400">Crawl unclaimed supplier sites</div>
                </div>
                {enrich?.status === 'running' && <span className="text-[11px] text-[#00A79D] font-semibold">{enrich.processed || 0}/{enrich.total || 0}</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={runHunter} data-testid="bulk-hunter" className="cursor-pointer">
                <MailSearch className="w-4 h-4 mr-2 text-slate-500" />
                <div className="flex-1">
                  <div>Enrich via Hunter</div>
                  <div className="text-[11px] text-slate-400">Fill supplier blanks via Hunter.io</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-slate-400 uppercase tracking-wider">Care providers</DropdownMenuLabel>
              <DropdownMenuItem onSelect={runFindDomains} disabled={domains?.status === 'running'} data-testid="bulk-find-domains" className="cursor-pointer">
                <Globe className="w-4 h-4 mr-2 text-slate-500" />
                <div className="flex-1">
                  <div>Find provider websites</div>
                  <div className="text-[11px] text-slate-400">Discover sites by name + location (free)</div>
                </div>
                {domains?.status === 'running' && <span className="text-[11px] text-[#8A5A00] font-semibold">{domains.processed || 0}/{domains.total || 0}</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={runCareProviders} disabled={care?.status === 'running'} data-testid="bulk-enrich-providers" className="cursor-pointer">
                <MailSearch className="w-4 h-4 mr-2 text-slate-500" />
                <div className="flex-1">
                  <div>Enrich care providers</div>
                  <div className="text-[11px] text-slate-400">Crawl site, then Hunter fallback</div>
                </div>
                {care?.status === 'running' && <span className="text-[11px] text-[#0B6E4F] font-semibold">{care.processed || 0}/{care.total || 0}</span>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={linkLists} disabled={!!busy} data-testid="bulk-link-lists"><Users className="w-4 h-4 mr-1" />Link list emails</Button>
          <Button variant="outline" onClick={importSupplierContacts} disabled={!!busy} data-testid="bulk-import-suppliers"><Users className="w-4 h-4 mr-1" />Import supplier contacts</Button>
          <Button variant="outline" onClick={runClaimNudge} data-testid="bulk-claim-nudge"><Users className="w-4 h-4 mr-1" />Claim nudge</Button>
          {abandoned > 0 && (
            <Button variant="outline" onClick={recoverAbandoned} className="text-amber-700 border-amber-300" data-testid="bulk-recover-abandoned">
              <RefreshCw className="w-4 h-4 mr-1" />Recover abandoned ({abandoned})
            </Button>
          )}
          <Button onClick={openNew} className="bg-[#00A79D] hover:bg-[#00938a] text-white" data-testid="bulk-new-campaign"><Plus className="w-4 h-4 mr-1" />New campaign</Button>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-slate-200" data-testid="bulk-tabs">
        <button
          onClick={() => setTab('campaigns')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === 'campaigns' ? 'border-[#00A79D] text-[#00A79D]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="bulk-tab-campaigns">
          <Mail className="w-4 h-4 mr-1.5 inline-block" />Campaigns
        </button>
        <button
          onClick={() => setTab('automations')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === 'automations' ? 'border-[#00A79D] text-[#00A79D]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="bulk-tab-automations">
          <Sparkles className="w-4 h-4 mr-1.5 inline-block" />Webinar automations
        </button>
      </div>

      {tab === 'automations' && <WebinarAutomations api={API} />}

      {tab === 'campaigns' && (<>
      {enrich && enrich.status !== 'idle' && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5" data-testid="bulk-enrich-status">
          <MailSearch className="w-4 h-4 text-[#00A79D]" />
          <span className="text-slate-600">
            Unclaimed suppliers: <b className="text-slate-900">{enrich.with_email || 0}</b> of {enrich.suppliers_total || 0} have a contact email
            {typeof enrich.with_website === 'number' ? ` · ${enrich.with_website} have a website to crawl` : ''}.
          </span>
          {enrich.status === 'running' && <span className="text-[#00A79D] font-semibold ml-auto">Crawling {enrich.processed || 0}/{enrich.total || 0} · found {enrich.found || 0}</span>}
          {enrich.status === 'done' && <span className="text-emerald-600 font-semibold ml-auto">Last run{enrich.finished_at ? ` (${new Date(enrich.finished_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })})` : ''} found {enrich.found || 0} new emails</span>}
          {enrich.status === 'stalled' && <span className="text-amber-600 font-semibold ml-auto" data-testid="bulk-enrich-stalled">Interrupted{enrich.updated_at ? ` (${new Date(enrich.updated_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})` : ''} at {enrich.processed || 0}/{enrich.total || 0} - open Enrichment to resume</span>}
        </div>
      )}

      {care && care.status !== 'idle' && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5" data-testid="bulk-care-enrich-status">
          <MailSearch className="w-4 h-4 text-[#0B6E4F]" />
          <span className="text-slate-600">
            Care providers: <b className="text-slate-900">{care.with_email || 0}</b> of {care.providers_total || 0} have a contact email
            {typeof care.with_website === 'number' ? ` · ${care.with_website} have a website to crawl` : ''}.
          </span>
          {care.status === 'running' && (
            <span className="text-[#0B6E4F] font-semibold ml-auto">
              Enriching {care.processed || 0}/{care.total || 0} · found {care.found || 0} <span className="font-normal text-slate-500">({care.found_web || 0} web · {care.found_hunter || 0} Hunter)</span>
            </span>
          )}
          {care.status === 'done' && (
            <span className="text-emerald-600 font-semibold ml-auto">
              Last run{care.finished_at ? ` (${new Date(care.finished_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })})` : ''} found {care.found || 0} <span className="font-normal text-slate-500">({care.found_web || 0} web · {care.found_hunter || 0} Hunter)</span>
            </span>
          )}
          {care.status === 'stalled' && <span className="text-amber-600 font-semibold ml-auto" data-testid="bulk-care-stalled">Interrupted{care.updated_at ? ` (${new Date(care.updated_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})` : ''} at {care.processed || 0}/{care.total || 0} - open Enrichment to resume</span>}
        </div>
      )}

      {domains && domains.status !== 'idle' && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5" data-testid="bulk-find-domains-status">
          <MailSearch className="w-4 h-4 text-[#8A5A00]" />
          <span className="text-slate-600">
            Provider websites: <b className="text-slate-900">{domains.with_website || 0}</b> of {domains.providers_total || 0} have a website
            {typeof domains.not_yet_tried === 'number' ? ` · ${domains.not_yet_tried} still to look up` : ''}.
          </span>
          {domains.status === 'running' && (
            <span className="text-[#8A5A00] font-semibold ml-auto">Searching {domains.processed || 0}/{domains.total || 0} · found {domains.found || 0} websites</span>
          )}
          {domains.status === 'done' && (
            <span className="text-emerald-600 font-semibold ml-auto">Last run{domains.finished_at ? ` (${new Date(domains.finished_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })})` : ''} found {domains.found || 0} new websites</span>
          )}
          {domains.status === 'stalled' && <span className="text-amber-600 font-semibold ml-auto" data-testid="bulk-domains-stalled">Interrupted{domains.updated_at ? ` (${new Date(domains.updated_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})` : ''} at {domains.processed || 0}/{domains.total || 0} - open Enrichment to resume</span>}
        </div>
      )}

      {bestTimes && (
        <div className="mb-6 bg-white border border-slate-200 rounded-xl p-4" data-testid="best-send-times">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-[#00A79D]" />
            <h3 className="text-sm font-semibold text-[#0B2942]">Best time to send</h3>
            {bestTimes.total_events > 0 ? (
              <span className="text-xs text-slate-500 ml-2">
                Your audience engages most on <b className="text-[#00857D]">{bestTimes.best_weekday}</b> around <b className="text-[#00857D]">{String(bestTimes.best_hour).padStart(2, '0')}:00 UK</b>
              </span>
            ) : (
              <span className="text-xs text-slate-400 ml-2">Not enough open data yet - appears once your campaigns get opens.</span>
            )}
          </div>
          {bestTimes.total_events > 0 && (
            <div className="flex items-end justify-between gap-2 h-24" data-testid="best-times-weekday">
              {(() => {
                const max = Math.max(1, ...bestTimes.by_weekday.map((d) => d.opens));
                return bestTimes.by_weekday.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[10px] text-slate-500">{d.opens}</span>
                    <div className="w-full bg-slate-100 rounded-t flex items-end" style={{ height: '60px' }}>
                      <div className={`w-full rounded-t transition-all ${d.day === bestTimes.best_weekday ? 'bg-[#00A79D]' : 'bg-slate-300'}`} style={{ height: `${(d.opens / max) * 100}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-400">{d.day}</span>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        {/* Campaign list */}
        <div className="space-y-2" data-testid="bulk-campaign-list">
          {campaigns.length === 0 && <p className="text-sm text-slate-400 p-4">No campaigns yet.</p>}
          {campaigns.map((c) => (
            <div key={c.id}
              onClick={() => openCampaign(c)}
              className={`p-4 rounded-xl border cursor-pointer transition-colors ${sel?.id === c.id ? 'border-[#00A79D] bg-[#00A79D]/5' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
              data-testid={`bulk-campaign-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <span className="font-semibold text-[#0B2942] text-sm truncate">{c.name}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); duplicate(c); }}
                    title="Duplicate this campaign"
                    className="text-slate-400 hover:text-[#00A79D] transition-colors p-0.5"
                    data-testid={`bulk-duplicate-${c.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  {canPause(c) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePause(c); }}
                      title={c.paused ? 'Resume this campaign' : 'Pause this campaign'}
                      className={`p-0.5 transition-colors ${c.paused ? 'text-orange-600 hover:text-[#00A79D]' : 'text-slate-400 hover:text-orange-600'}`}
                      data-testid={`bulk-pause-toggle-${c.id}`}>
                      {c.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  {c.paused && <Badge className="bg-orange-100 text-orange-800 border-0 text-[11px]" data-testid={`bulk-paused-badge-${c.id}`}>paused</Badge>}
                  <Badge className={`${STATUS_STYLES[c.status] || 'bg-slate-100'} border-0 text-[11px]`}>{c.status}</Badge>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1 truncate">{c.subject || 'No subject'}</p>
              <div className="flex items-center justify-between mt-2 text-[11px] text-slate-400">
                <span>{(() => {
                  const a = c.audience || {};
                  const ids = a.lists || (a.type === 'crm_list' && a.list_id ? [a.list_id] : a.type === 'combo' ? (a.include || []).map((i) => i.list_id) : []);
                  if (ids.length) { const nm = lists.filter((l) => ids.includes(l.id)).map((l) => l.name); return nm.length ? (nm.length > 1 ? `${nm.length} lists` : nm[0]) : `${ids.length} list(s)`; }
                  return (AUDIENCE_TYPES.find((x) => x.type === a.type) || {}).label || a.type || 'No audience';
                })()}</span>
                {c.status === 'scheduled' && c.scheduled_at && (
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(c.scheduled_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}</span>)}
                {(c.status === 'sent' || c.status === 'sending') && (
                  <span>{c.sent_count}/{c.total_recipients} sent{c.failed_count ? ` · ${c.failed_count} failed` : ''}</span>)}
              </div>
            </div>
          ))}
        </div>

        {/* Editor */}
        {!sel ? (
          <div className="flex items-center justify-center border border-dashed border-slate-200 rounded-2xl min-h-[400px] text-slate-400">
            Select a campaign or create a new one
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5" data-testid="bulk-editor">
            <div className="flex items-center justify-between">
              <input
                value={sel.name} disabled={isLocked}
                onChange={(e) => setField('name', e.target.value)}
                className="text-lg font-bold text-[#0B2942] bg-transparent border-b border-transparent focus:border-slate-300 outline-none w-full"
                data-testid="bulk-name" />
              {sel.id && <Button variant="ghost" size="sm" onClick={() => duplicate(sel)} className="text-slate-500" title="Duplicate this campaign" data-testid="bulk-duplicate"><Copy className="w-4 h-4" /></Button>}
              {sel.id && <Button variant="ghost" size="sm" onClick={() => remove(sel.id)} className="text-red-500" data-testid="bulk-delete"><Trash2 className="w-4 h-4" /></Button>}
            </div>

            {/* Audience - dropdown multiselect of CRM lists (stackable) */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recipient lists</label>
              <p className="text-[11px] text-slate-400 mt-1">Select one or more lists - recipients are combined (de-duplicated automatically).</p>
              <Popover open={listMenuOpen} onOpenChange={setListMenuOpen}>
                <PopoverTrigger asChild>
                  <button type="button" disabled={isLocked} data-testid="bulk-lists-trigger"
                    className="mt-2 w-full flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-left hover:border-slate-300 disabled:opacity-60 disabled:cursor-not-allowed">
                    <span className="flex-1 truncate">
                      {sel.audience.type === 'paste'
                        ? 'Pasted list of addresses'
                        : selectedListIds.length === 0
                          ? <span className="text-slate-400">Select recipient lists…</span>
                          : `${selectedListIds.length} list${selectedListIds.length > 1 ? 's' : ''} selected`}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[320px]">
                  <Command>
                    <CommandInput placeholder="Search lists…" data-testid="bulk-lists-search" />
                    <CommandList>
                      <CommandEmpty>No lists found.</CommandEmpty>
                      <CommandGroup heading="Contact lists">
                        {lists.map((l) => {
                          const on = selectedListIds.includes(l.id);
                          return (
                            <CommandItem key={l.id} value={l.name} onSelect={() => toggleList(l.id)}
                              disabled={isLocked} className="cursor-pointer" data-testid={`bulk-list-${l.id}`}>
                              <Checkbox checked={on} className="mr-2 pointer-events-none" />
                              <span className="flex-1 truncate">{l.name}</span>
                              {typeof l.member_count === 'number' && (
                                <span className="text-xs text-slate-400 ml-2">{l.member_count}</span>)}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem value="pasted list of addresses" data-testid="bulk-audience-paste"
                          className="cursor-pointer"
                          onSelect={() => { setSel((p) => ({ ...p, audience: { type: 'paste', emails: p.audience?.emails || '', lists: [] } })); setPreview(null); setListMenuOpen(false); }}>
                          <Checkbox checked={sel.audience.type === 'paste'} className="mr-2 pointer-events-none" />
                          <span>Pasted list of addresses</span>
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {sel.audience.type !== 'paste' && selectedListIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="bulk-lists-selected">
                  {selectedListIds.map((id) => {
                    const l = lists.find((x) => x.id === id);
                    return (
                      <Badge key={id} variant="secondary" className="gap-1 pl-2.5 pr-1 py-1">
                        {l?.name || id}
                        <button type="button" disabled={isLocked} onClick={() => toggleList(id)}
                          className="rounded-full hover:bg-slate-300/60 p-0.5" data-testid={`bulk-list-remove-${id}`} aria-label={`Remove ${l?.name || id}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
              {lists.length === 0 && (
                <p className="text-xs text-slate-400 mt-2">No contact lists yet - create one in Lists.</p>)}
              {selectedListIds.length > 1 && (
                <p className="text-xs text-[#00857D] font-semibold mt-2" data-testid="bulk-lists-stacked">{selectedListIds.length} lists stacked</p>)}
              {sel.audience.type === 'paste' && (
                <Textarea rows={4} placeholder="Paste emails separated by commas, spaces or new lines"
                  value={sel.audience.emails || ''} disabled={isLocked}
                  onChange={(e) => setAud('emails', e.target.value)}
                  className="mt-2" data-testid="bulk-paste-emails" />
              )}
              <div className="flex items-center gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={doPreview} data-testid="bulk-preview-btn"><Eye className="w-4 h-4 mr-1" />Preview recipients</Button>
                {preview && <span className="text-sm text-[#00A79D] font-semibold" data-testid="bulk-preview-count">{preview.count} recipients</span>}
                {sel.audience.type !== 'paste' && listName === null && (
                  <Button variant="ghost" size="sm" className="ml-auto text-[#00857D]" onClick={() => setListName(selectedListIds.length > 1 ? `${selectedListIds.map((id) => lists.find((x) => x.id === id)?.name || 'List').join(' + ')}`.slice(0, 120) : '')} data-testid="bulk-save-as-list-btn">
                    <ListPlus className="w-4 h-4 mr-1" />Save selection as list</Button>)}
              </div>
              {listName !== null && (
                <div className="flex items-center gap-2 mt-2" data-testid="bulk-save-as-list-form">
                  <Input value={listName} onChange={(e) => setListName(e.target.value)} placeholder="New list name" className="h-9" data-testid="bulk-save-as-list-name" />
                  <Button size="sm" onClick={saveAsList} disabled={savingList} data-testid="bulk-save-as-list-confirm">{savingList ? 'Saving...' : 'Save list'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setListName(null)} data-testid="bulk-save-as-list-cancel">Cancel</Button>
                </div>)}
              {preview?.sample?.length > 0 && (
                <p className="text-xs text-slate-400 mt-1 truncate">e.g. {preview.sample.join(', ')}</p>)}
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Content</label>
                  {!isLocked && (
                    <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="bulk-mode-toggle">
                      <button type="button" onClick={() => setComposeMode('design')}
                        className={`px-3 py-1 text-xs font-semibold ${composeMode === 'design' ? 'bg-[#00A79D] text-white' : 'bg-white text-slate-500'}`}
                        data-testid="bulk-mode-design">Design</button>
                      <button type="button" onClick={() => setComposeMode('html')}
                        className={`px-3 py-1 text-xs font-semibold ${composeMode === 'html' ? 'bg-[#00A79D] text-white' : 'bg-white text-slate-500'}`}
                        data-testid="bulk-mode-html">HTML</button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {templates.length > 0 && (
                    <select onChange={(e) => e.target.value && applyTemplate(e.target.value)} disabled={isLocked}
                      className="border border-slate-200 rounded-lg px-2 py-1 text-xs" defaultValue="" data-testid="bulk-template-select">
                      <option value="">Load template…</option>
                      {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  )}
                  {!isLocked && <Button variant="ghost" size="sm" onClick={saveTemplate} className="text-slate-500" data-testid="bulk-save-template"><Save className="w-4 h-4 mr-1" />Save template</Button>}
                </div>
              </div>
              <Input placeholder="Subject line" value={sel.subject} disabled={isLocked}
                onChange={(e) => setField('subject', e.target.value)} className="mt-2" data-testid="bulk-subject" />
              <p className="text-[10px] text-slate-400 mt-1">Personalise with <code className="bg-slate-100 px-1 rounded">[Name]</code> and <code className="bg-slate-100 px-1 rounded">[Company]</code> - each recipient gets their own (falls back to "there" / "your organisation").</p>
              <div className="mt-3">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Send from</label>
                <SenderSelect value={sel.sender_member_id || ''} disabled={isLocked} testid="bulk-from-select"
                  onChange={(id, m) => setSel((p) => ({ ...p, sender_member_id: id, from_name: m ? m.name : 'Care Tech Compare', from_email: m ? m.sender_email : '', reply_to: m ? m.sender_email : '' }))} />
              </div>
              {composeMode === 'design' && !isLocked ? (
                <div className="mt-3">
                  <EmailBuilder blocks={sel.design_json || []} onChange={setBlocks} />
                  <p className="text-[10px] text-slate-400 mt-2">Drag blocks from the left, click text to edit inline, style each block on the right. The sender's signature + an unsubscribe link are added automatically.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <Textarea rows={12} placeholder="<p>Write your email in HTML…</p>" value={sel.html_body} disabled={isLocked}
                    onChange={(e) => setField('html_body', e.target.value)} className="font-mono text-xs" data-testid="bulk-body" />
                  <div className="border border-slate-200 rounded-lg p-4 overflow-auto max-h-[320px] bg-slate-50">
                    <p className="text-[10px] uppercase text-slate-400 mb-2">Preview</p>
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sel.html_body || '<p class="text-slate-400">Nothing yet…</p>' }} />
                    <p className="text-[10px] text-slate-400 mt-3 border-t pt-2">The sender's signature + an unsubscribe link are added automatically.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Attachments */}
            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Attachments</label>
                {!isLocked && (
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] cursor-pointer hover:underline" data-testid="bulk-attach-label">
                    <Paperclip className="w-3.5 h-3.5" /> Add file
                    <input type="file" className="hidden" data-testid="bulk-attach-input"
                      onChange={(e) => { uploadAttachment(e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                )}
              </div>
              {(sel.attachments || []).length === 0 ? (
                <p className="text-xs text-slate-400 mt-1">No attachments. Files up to 10 MB each (PDF, docs, images…).</p>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="bulk-attachments">
                  {(sel.attachments || []).map((a) => (
                    <span key={a.id} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 border border-slate-200 rounded-full pl-3 pr-2 py-1" data-testid={`bulk-attachment-${a.id}`}>
                      <Paperclip className="w-3 h-3 text-slate-500" />
                      <span className="text-slate-700 max-w-[180px] truncate">{a.filename}</span>
                      <span className="text-slate-400">({fmtSize(a.size)})</span>
                      {!isLocked && <button type="button" onClick={() => removeAttachment(a.id)} className="text-slate-400 hover:text-red-500 ml-0.5" data-testid={`bulk-attachment-remove-${a.id}`}>✕</button>}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {!isLocked && (
              <>
                {/* Test send */}
                <div className="flex items-end gap-2 border-t border-slate-100 pt-4">
                  <div className="flex-1">
                    <label className="text-xs font-semibold text-slate-500">Send a test to yourself</label>
                    <Input placeholder="you@example.com" value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)} className="mt-1" data-testid="bulk-test-email" />
                  </div>
                  <Button variant="outline" onClick={testSend} disabled={busy} data-testid="bulk-test-send"><FlaskConical className="w-4 h-4 mr-1" />Test send</Button>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4">
                  <Button variant="outline" onClick={save} disabled={busy} data-testid="bulk-save"><Save className="w-4 h-4 mr-1" />Save draft</Button>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block">Schedule (UK time)</label>
                    <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                      className="mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="bulk-schedule-at" />
                  </div>
                  {bestTimes?.best_weekday && (
                    <Button variant="outline" onClick={suggestSlot} disabled={busy} className="text-[#00857D] border-[#00A79D]/40" data-testid="bulk-best-slot">
                      <Clock className="w-4 h-4 mr-1" />Use best slot
                    </Button>
                  )}
                  <Button onClick={doSchedule} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="bulk-schedule-btn"><Clock className="w-4 h-4 mr-1" />Schedule</Button>
                  <Button onClick={sendNow} disabled={busy} className="bg-[#00A79D] hover:bg-[#00938a] text-white" data-testid="bulk-send-now"><Send className="w-4 h-4 mr-1" />Send now</Button>
                </div>
              </>
            )}

            {canPause(sel) && (
              <div className={`flex items-center justify-between gap-3 rounded-lg px-4 py-3 border ${sel.paused ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`} data-testid="bulk-pause-banner">
                <span className={`text-sm flex items-center gap-2 ${sel.paused ? 'text-orange-800' : 'text-slate-600'}`}>
                  {sel.paused ? <><Pause className="w-4 h-4 shrink-0" />This campaign is paused. Nothing more will send until you resume it.</> : 'Need to stop just this campaign? Pause it without pausing all emails.'}
                </span>
                <Button variant="outline" size="sm" onClick={() => togglePause(sel)} className={sel.paused ? 'text-[#00857D] border-[#00A79D]/40' : 'text-orange-700 border-orange-300'} data-testid="bulk-pause-btn">
                  {sel.paused ? <><Play className="w-4 h-4 mr-1" />Resume</> : <><Pause className="w-4 h-4 mr-1" />Pause campaign</>}
                </Button>
              </div>
            )}
            {sel.status === 'scheduled' && (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="bulk-scheduled-banner">
                <span className="text-sm text-amber-800 flex items-center gap-2"><Clock className="w-4 h-4" />Scheduled for {new Date(sel.scheduled_at).toLocaleString('en-GB')}</span>
                <Button variant="ghost" size="sm" onClick={cancelSchedule} className="text-amber-800" data-testid="bulk-cancel-schedule"><Ban className="w-4 h-4 mr-1" />Cancel</Button>
              </div>
            )}
            {isLocked && (
              <div className="space-y-3" data-testid="bulk-sent-banner">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800">
                  {sel.status === 'sending' ? 'Sending…' : 'Sent'} - {sel.sent_count}/{sel.total_recipients} delivered{sel.failed_count ? `, ${sel.failed_count} failed` : ''}.
                </div>
                {stats && (
                  <div data-testid="bulk-stats">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-[#0B2942]">{stats.sent}</p>
                        <p className="text-xs text-slate-500 mt-1">Delivered</p>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center" data-testid="bulk-stat-opens">
                        <p className="text-2xl font-bold text-[#00A79D]">{stats.open_rate}%</p>
                        <p className="text-xs text-slate-500 mt-1">Opens ({stats.opens_unique} unique)</p>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center" data-testid="bulk-stat-clicks">
                        <p className="text-2xl font-bold text-amber-500">{stats.click_rate}%</p>
                        <p className="text-xs text-slate-500 mt-1">Clicks ({stats.clicks_unique} unique)</p>
                      </div>
                    </div>
                    {stats.top_links?.length > 0 && (
                      <div className="mt-3 bg-white border border-slate-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Top links</p>
                        {stats.top_links.map((l, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                            <span className="truncate text-slate-600 mr-3">{l.url}</span>
                            <span className="font-semibold text-slate-800 whitespace-nowrap">{l.clicks} clicks</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-slate-400 mt-2">Opens are approximate (some clients block tracking pixels). Refreshes automatically.</p>
                  </div>
                )}

                <div className="bg-white border border-slate-200 rounded-xl" data-testid="bulk-recipients-panel">
                  <button
                    onClick={() => setShowRecips((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-[#0B2942]"
                    data-testid="bulk-recipients-toggle">
                    <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#00A79D]" /> Who opened / clicked</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showRecips ? 'rotate-180' : ''}`} />
                  </button>
                  {showRecips && (
                    <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        {[['opened', 'Opened'], ['clicked', 'Clicked'], ['not_opened', 'Not opened'], ['all', 'Everyone']].map(([k, label]) => (
                          <button key={k} onClick={() => setRecipFilter(k)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${recipFilter === k ? 'bg-[#00A79D] border-[#00A79D] text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-[#00A79D]'}`}
                            data-testid={`bulk-recip-tab-${k}`}>
                            {label}{recips?.counts ? ` (${recips.counts[k]})` : ''}
                          </button>
                        ))}
                        <div className="flex-1 min-w-[140px]" />
                        <Button variant="outline" size="sm" onClick={downloadRecipCsv} data-testid="bulk-recip-csv">
                          <Save className="w-4 h-4 mr-1" />CSV
                        </Button>
                      </div>
                      <div className="flex gap-2 mb-3">
                        <Input placeholder="Search name, email or company"
                          value={recipSearch} onChange={(e) => setRecipSearch(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && fetchRecips(0)}
                          className="h-9 text-sm" data-testid="bulk-recip-search" />
                        <Button size="sm" onClick={() => fetchRecips(0)} data-testid="bulk-recip-search-btn">Search</Button>
                      </div>
                      {recipLoading ? (
                        <div className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
                      ) : recips?.rows?.length ? (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="bulk-recip-table">
                              <thead>
                                <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                                  <th className="py-2 pr-3 font-medium">Recipient</th>
                                  <th className="py-2 px-2 font-medium text-center">Opened</th>
                                  <th className="py-2 px-2 font-medium text-center">Clicked</th>
                                  <th className="py-2 pl-2 font-medium">Delivery</th>
                                </tr>
                              </thead>
                              <tbody>
                                {recips.rows.map((r) => (
                                  <tr key={r.email} className="border-b border-slate-50 last:border-0" data-testid="bulk-recip-row">
                                    <td className="py-2 pr-3">
                                      <div className="font-medium text-slate-800">{r.name || r.email}</div>
                                      {(r.name || r.company) && <div className="text-xs text-slate-400">{r.email}{r.company ? ` · ${r.company}` : ''}</div>}
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      {r.opened
                                        ? <span className="text-[#00A79D] font-semibold text-xs" title={r.opened_at || ''}>✓{r.open_count > 1 ? ` ×${r.open_count}` : ''}</span>
                                        : <span className="text-slate-300"> - </span>}
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      {r.clicked
                                        ? <span className="text-amber-500 font-semibold text-xs" title={r.clicked_at || ''}>✓{r.click_count > 1 ? ` ×${r.click_count}` : ''}</span>
                                        : <span className="text-slate-300"> - </span>}
                                    </td>
                                    <td className="py-2 pl-2">
                                      {(() => {
                                        const s = r.status;
                                        const label = s === 'failed' ? 'Failed' : s === 'bounced' ? 'Bounced' : s === 'complained' ? 'Complaint' : s === 'delivered' ? 'Delivered' : 'Sent';
                                        const bad = s === 'failed' || s === 'bounced' || s === 'complained';
                                        return <span className={`text-xs ${bad ? 'text-red-500 font-semibold' : 'text-slate-500'}`}>{label}</span>;
                                      })()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                            <span>{recips.total} {recipFilter === 'all' ? 'recipients' : recipFilter.replace('_', ' ')}</span>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" disabled={recipPage === 0} onClick={() => fetchRecips(recipPage - 1)}>Prev</Button>
                              <span>Page {recipPage + 1} / {Math.max(1, Math.ceil(recips.total / RECIP_PAGE))}</span>
                              <Button variant="outline" size="sm" disabled={(recipPage + 1) * RECIP_PAGE >= recips.total} onClick={() => fetchRecips(recipPage + 1)}>Next</Button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="py-6 text-center text-sm text-slate-400" data-testid="bulk-recip-empty">No recipients match this filter yet.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}
